import { type ReadonlyVec2, vec2 } from 'gl-matrix';

import { vec2extended } from '@canvas/math/vec2';
import { getPointAndTangentOnPath, type PathSegment } from '@canvas/path';

export type ArrowheadSpatialData = {
  position: ReadonlyVec2;
  size: ReadonlyVec2;
  rotation: number;
};

export function createArrowheadSpatialData(): ArrowheadSpatialData {
  return {
    position: vec2.create(),
    size: vec2.create(),
    rotation: 0,
  };
}

/**
 * Reutrns position, size and rotation for an arrowhead to be placed along a path.
 *
 * @param pathStart the start position of the path
 * @param pathSegments the segments defining the path shape
 * @param proportion the distance along the path to place the arrowhead (0 - 1)
 * @param arrowheadSize the size of the arrowhead
 * @param offsetFromEnd the amount to offset from the end of the path
 * @param flipOrientation set to true to flip the orientation of the arrowhead
 * @param result an optional object to write the result values to
 * @returns the result value object
 */
export function getSpatialDataForArrowheadOnPath(
  pathStart: ReadonlyVec2,
  pathSegments: PathSegment[],
  proportion: number,
  arrowheadSize: ReadonlyVec2,
  offsetFromEnd: number,
  flipOrientation: boolean,
  result: ArrowheadSpatialData = createArrowheadSpatialData(),
): ArrowheadSpatialData {
  const [position, computedRotation] = getPointAndTangentOnPath(
    pathStart,
    pathSegments,
    proportion,
  );

  // Rotate the rotation by 180 degrees if flipping orientation
  const rotation = flipOrientation ? computedRotation : computedRotation + Math.PI;

  // Calculate offset from end path - depending on the size of the arrowhead.
  const offsetFromEndVec2 = vec2extended.fromPolar(rotation, offsetFromEnd);
  vec2.sub(position, position, offsetFromEndVec2);

  // Calculate centering offset - perpendicular to the path direction.
  const centeringOffset = vec2extended.fromPolar(
    rotation + Math.PI / 2,
    arrowheadSize[1] * 0.5,
  );

  // Add this to position to create position with centering offset
  const positionWithCenteringOffset = vec2.add(
    centeringOffset,
    position,
    centeringOffset,
  );

  const size = arrowheadSize;

  // update values on the flyweight object and return it
  result.position = positionWithCenteringOffset;
  result.size = size;
  result.rotation = rotation;
  return result;
}
